import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, ApiError, type HrInsights, type HrWeeklyReport } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { HrStat } from "./_shared";
import { fmtMins } from "./_shared";

const ACCENT = "#9a3412";
const TEAL = "#5e7a73";

const BAND_CLS: Record<string, string> = {
  excellent: "pill-positive",
  good: "pill-positive",
  fair: "pill-neutral",
  poor: "pill-warn",
};

const fmtDate = (iso: string) =>
  new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString([], {
    day: "numeric",
    month: "short",
  });

export default function HeartReport() {
  const [ins, setIns] = useState<HrInsights | null | "error">(null);
  const [reports, setReports] = useState<HrWeeklyReport[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  function loadReports() {
    api.heart.reports().then((r) => setReports(r.reports)).catch(() => setReports([]));
  }
  useEffect(() => {
    api.heart.insights(30).then(setIns).catch(() => setIns("error"));
    loadReports();
  }, []);

  const d = ins && ins !== "error" ? ins : null;
  const nothing = d && d.fitness.vo2max == null && d.hour_pattern.every((h) => h.avg == null);

  async function generate() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.heart.generateReport();
      setMsg(`Report ready for week of ${fmtDate(r.week_start)}.`);
      loadReports();
    } catch (e) {
      setMsg(e instanceof ApiError ? `Couldn't generate (API ${e.status})` : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="animate-fade-in">
      <h2 className="section-title mb-1">Heart report</h2>
      <p className="text-ink-subtle text-small mb-4">
        Wellness insights computed from your heart rate — estimates and signals, not medical advice.
      </p>

      {nothing ? (
        <EmptyState error={ins === "error"} title="Not enough heart-rate data yet">
          Once a week or two of heart rate accumulates, your cardio-fitness estimate, resting-rate
          signals and daily patterns appear here.
        </EmptyState>
      ) : (
        d && (
          <div className="space-y-6">
            {/* Cardio fitness */}
            <section className="card card-pad">
              <div className="flex items-start justify-between">
                <div>
                  <div className="eyebrow mb-1">Estimated cardio fitness</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-4xl tabular-nums text-ink">
                      {d.fitness.vo2max ?? "—"}
                    </span>
                    <span className="text-sm text-ink-subtle">VO₂max ml/kg/min (est.)</span>
                  </div>
                  <div className="mt-1 text-sm text-ink-subtle">
                    from resting {d.fitness.resting_used ?? "—"} bpm · typical for your age ≈{" "}
                    {d.fitness.expected}
                  </div>
                </div>
                {d.fitness.band && <span className={BAND_CLS[d.fitness.band]}>{d.fitness.band}</span>}
              </div>
              {d.fitness.series.length > 1 && (
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={d.fitness.series} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
                    <YAxis hide domain={["auto", "auto"]} />
                    <Tooltip formatter={(v: number) => [`${v}`, "VO₂max"]} labelFormatter={(l) => fmtDate(String(l))} />
                    <Line type="monotone" dataKey="vo2max" stroke={TEAL} strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </section>

            {/* Resting-HR signal */}
            <section>
              <h3 className="field-label mb-2">Resting-rate signal</h3>
              <div className="card card-pad">
                {d.anomalies.days.length === 0 ? (
                  <p className="muted text-sm">
                    No elevated-resting-HR days lately — your baseline (~{d.anomalies.baseline_recent ?? "—"} bpm) is
                    holding steady. A spike here can hint at stress, poor sleep, or a bug coming on.
                  </p>
                ) : (
                  <>
                    <p className="muted mb-2 text-sm">
                      Days your resting HR ran notably above your ~{d.anomalies.baseline_recent} bpm baseline
                      (stress / illness / under-recovery signal):
                    </p>
                    <ul className="divide-y divide-ink-line-soft">
                      {d.anomalies.days.slice(-6).reverse().map((a) => (
                        <li key={a.date} className="flex items-center justify-between py-1.5 text-sm">
                          <span>{fmtDate(a.date)}</span>
                          <span className="tabular-nums text-ink-muted">
                            {a.resting_hr} bpm <span className="text-loss">▲ +{a.over_by}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </section>

            {/* Daily rhythm */}
            <section>
              <h3 className="field-label mb-2">Daily rhythm</h3>
              <div className="card card-pad">
                <div className="mb-1 text-xs text-ink-subtle">Average HR by hour of day</div>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={d.hour_pattern}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                    <XAxis dataKey="hour" axisLine={false} tickLine={false} fontSize={10} interval={2} tickFormatter={(h) => `${h}`} />
                    <YAxis axisLine={false} tickLine={false} width={28} fontSize={10} domain={["auto", "auto"]} />
                    <Tooltip formatter={(v: number) => [`${v} bpm`, "Avg"]} labelFormatter={(h) => `${h}:00`} />
                    <Line type="monotone" dataKey="avg" stroke={ACCENT} strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
                {d.sleeping_hr != null && (
                  <div className="mt-2 text-xs text-ink-subtle">
                    Lowest overnight (00:00–06:00): <span className="text-ink tabular-nums">{d.sleeping_hr} bpm</span>
                  </div>
                )}
              </div>
            </section>

            {/* Day of week */}
            <section>
              <h3 className="field-label mb-2">By day of week</h3>
              <div className="card card-pad">
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={d.day_pattern}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                    <XAxis dataKey="label" axisLine={false} tickLine={false} fontSize={11} />
                    <YAxis axisLine={false} tickLine={false} width={28} fontSize={10} domain={["auto", "auto"]} />
                    <Tooltip formatter={(v: number) => [`${v} bpm`, "Avg"]} />
                    <Bar dataKey="avg" radius={[3, 3, 0, 0]}>
                      {d.day_pattern.map((_, i) => (
                        <Cell key={i} fill={TEAL} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            {/* This week's zones */}
            <section>
              <h3 className="field-label mb-2">This week in zones</h3>
              <div className="card card-pad space-y-1.5">
                {[...d.zones_week].reverse().map((z) => (
                  <div key={z.name} className="flex items-center gap-2 text-sm">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: z.color }} />
                    <span className="w-20 text-ink">{z.name}</span>
                    <span className="tabular-nums text-ink-muted">{fmtMins(z.min)}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )
      )}

      {/* Weekly report archive */}
      <section className="mt-8">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="field-label">Weekly reports</h3>
          <button type="button" className="btn-secondary" onClick={generate} disabled={busy}>
            {busy ? "Generating…" : "Generate this week"}
          </button>
        </div>
        {msg && <p className="mb-2 text-sm text-ink-muted">{msg}</p>}
        {reports && reports.length === 0 ? (
          <div className="card card-pad">
            <p className="muted text-sm">
              A snapshot is saved automatically each week (Sundays). Tap “Generate this week” to make
              one now.
            </p>
          </div>
        ) : (
          <div className="card divide-y divide-ink-line-soft">
            {(reports ?? []).map((r) => (
              <div key={r.week_start} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink">Week of {fmtDate(r.week_start)}</span>
                  {r.summary.fitness_band && (
                    <span className={BAND_CLS[r.summary.fitness_band] ?? "pill-neutral"}>
                      {r.summary.fitness_band}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-ink-subtle">
                  rest {r.summary.avg_resting ?? "—"} bpm · VO₂max {r.summary.vo2max ?? "—"} ·{" "}
                  {fmtMins(r.summary.active_minutes)} active · {r.summary.anomaly_days} elevated day
                  {r.summary.anomaly_days === 1 ? "" : "s"} · top zone {r.summary.dominant_zone ?? "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
